// Promo modal — Task G.1.
//
// Subscribes to `state.promoModal`. When `{open:true, feature:K}` is set,
// renders content from `PROMO_CONTENT[K]` (or a fallback) inside a modal
// dialog mounted at `#share-promo-modal-host`. Dismissed via close button,
// backdrop click, or Escape key. PostHog telemetry is wrapped in try/catch
// so the modal degrades gracefully when the analytics SDK isn't loaded.
// G.2-G.4 wire data-promo elements that dispatch state.promoModal updates.

import { subscribe, getState, setState } from '../state.js';
import { PROMO_CONTENT, type PromoContent } from '../promo-content.js';

export interface PromoModalHandle {
  unmount: () => void;
}

declare global {
  interface Window {
    posthog?: {
      capture: (event: string, props?: Record<string, unknown>) => void;
    };
  }
}

function track(event: string, props: Record<string, unknown>): void {
  try {
    window.posthog?.capture(event, props);
  } catch {
    /* swallow — telemetry must never crash the UI */
  }
}

function escapeHtml(s: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };
  return s.replace(/[&<>"']/g, (c) => map[c]!);
}

export function mountPromoModal(host: Element): PromoModalHandle {
  host.textContent = '';
  const root = document.createElement('div');
  root.className = 'rh-promo-modal';
  root.style.display = 'none';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  host.appendChild(root);

  const close = (): void => {
    const state = getState();
    const feature = state.promoModal?.feature;
    setState({ promoModal: { open: false } });
    if (feature) track('share.promo_close', { feature });
  };

  const renderContent = (feature: string): void => {
    const fallback = PROMO_CONTENT.settings!;
    const content: PromoContent = PROMO_CONTENT[feature] ?? fallback;
    root.innerHTML = `
      <div class="rh-promo-modal-backdrop" data-action="close"></div>
      <div class="rh-promo-modal-card" role="document">
        <h2>${escapeHtml(content.title)}</h2>
        <p>${escapeHtml(content.body)}</p>
        ${content.imageUrl ? `<img src="${escapeHtml(content.imageUrl)}" alt="" loading="lazy">` : ''}
        <div class="rh-promo-modal-actions">
          <button class="rh-promo-modal-close" type="button" data-action="close">Close</button>
          <a class="rh-promo-modal-cta" href="${escapeHtml(content.ctaUrl)}" target="_blank" rel="noopener" data-action="cta">${escapeHtml(content.ctaLabel)}</a>
        </div>
      </div>
    `;
    root.querySelectorAll('[data-action="close"]').forEach((el) =>
      el.addEventListener('click', close),
    );
    root
      .querySelector('[data-action="cta"]')
      ?.addEventListener('click', () => {
        track('share.promo_cta_click', { feature });
      });
    track('share.promo_click', { feature });
  };

  const onKey = (e: KeyboardEvent): void => {
    const state = getState();
    if (e.key === 'Escape' && state.promoModal?.open) close();
  };
  document.addEventListener('keydown', onKey);

  let lastOpenFeature: string | null = null;
  const unsub = subscribe(() => {
    const m = getState().promoModal;
    if (m?.open && m.feature) {
      // Avoid re-render on unrelated state changes — only re-render when
      // the feature flips or when we're transitioning from hidden -> shown.
      if (m.feature !== lastOpenFeature || root.style.display === 'none') {
        root.style.display = 'flex';
        renderContent(m.feature);
        lastOpenFeature = m.feature;
      }
    } else {
      root.style.display = 'none';
      lastOpenFeature = null;
    }
  });

  return {
    unmount: () => {
      unsub();
      document.removeEventListener('keydown', onKey);
      host.textContent = '';
    },
  };
}
